import { createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit'
import { isEqual, orderBy } from 'lodash'
import { API, graphqlOperation } from 'aws-amplify'
import * as queries from '../../graphql/queries'
import * as mutations from '../../graphql/mutations'
import { makeErrorToast, msg, makeSuccessToast } from '../../utils/makeToast'
import { fetchContacts } from './contacts'
import { fetchQuoteInfo, fetchRecapQuoteInfo } from './quotes'
import { fetchProjectChildren } from './projects'
import dayjs from 'dayjs'
import i18next from 'i18next'
import { fetchProposalsByTenantIdUtil } from '@src/utils/fetchUtils'

import { quoteTotalsSummary } from '../../utils/quote/quoteTotalsSummary/frenchApfpAndPostProdQuoteTotalsSummary'

export const fetchProposals = createAsyncThunk(
  'proposals/fetchProposals',
  async (arg, { getState, rejectWithValue }) => {
    const { auth, settings } = getState()
    try {
      const sort = settings.sort
      // const {
      //   data: {
      //     proposalsByTenantId: { items },
      //   },
      // } = await API.graphql(
      //   graphqlOperation(queries.proposalsByTenantId, {
      //     tenantId: auth.userInfo.tenantId,
      //     filter: { activeFlag: { eq: true } },
      //   })
      // )
      const proposalsData = await fetchProposalsByTenantIdUtil(auth.userInfo.tenantId)

      return proposalsData
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const createProposal = createAsyncThunk(
  'proposals/createProposal',
  async ({ quoteData, newQuote }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { auth, quotes } = getState()
      const {
        data: { getQuote: originalQuote2 },
      } = await API.graphql(graphqlOperation(queries.getQuote, { id: quoteData.id }))

      for (const property in newQuote) {
        if (!isEqual(newQuote[property], originalQuote2[property])) {
          originalQuote2[property] = newQuote[property]
        }
      }
      delete originalQuote2.recapInfo
      delete originalQuote2.quoteInfo
      delete originalQuote2.proposals
      delete originalQuote2._deleted
      delete originalQuote2._lastChangedAt
      const {
        data: { updateQuote: parsedUpdatedQuote },
      } = await API.graphql(graphqlOperation(mutations.updateQuote, { input: originalQuote2 }))
      let quoteInfo = await dispatch(fetchQuoteInfo({ quoteId: quoteData.id }))
      let recapQuoteInfo = await dispatch(
        fetchRecapQuoteInfo({ quoteId: quoteData.id, calculateRecapInfo: false })
      )
      const {
        data: { getProjects },
      } = await API.graphql(
        graphqlOperation(queries.getProjects, { id: quoteData.parentProjectId })
      )

      const quoteTotals = quoteTotalsSummary(quoteData, quoteInfo.payload, recapQuoteInfo.payload)
      let newProposal = {
        id: nanoid(),
        tenantId: auth.userInfo.tenantId,
        quoteId: quoteData.id,
        activeFlag: true,
        quoteName: quoteData.name,
        projectName: getProjects.name,
        status: 'Generate',
        total: quoteTotals.discount.totalAfterDiscount,
      }

      await API.graphql(graphqlOperation(mutations.createProposals, { input: newProposal }))
      await dispatch(fetchProjectChildren({ parentProjectId: quoteData.parentProjectId }))
      dispatch(fetchProposals())
      makeSuccessToast(i18next.t('proposals:toasts.createdProposal'))
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const updateProposal = createAsyncThunk(
  'proposals/updateProposal',
  async ({ newProposal, currentProposal }, { dispatch, rejectWithValue, getState }) => {
    const { auth, settings } = getState()
    try {
      const {
        data: { getCompany },
      } = await API.graphql(graphqlOperation(queries.getCompany, { id: auth.userInfo.tenantId }))
      const {
        data: { getContact },
      } = await API.graphql(graphqlOperation(queries.getContact, { id: newProposal.contact }))
      const {
        data: { getProposals },
      } = await API.graphql(graphqlOperation(queries.getProposals, { id: currentProposal.id }))
      delete getProposals._deleted
      delete getProposals._lastChangedAt
      delete getProposals.quote
      delete getProposals.invoices
      // let clientName = `${getContact.firstName} ${getContact.lastName}`
      let clientName = `${newProposal.contactFirstName} ${newProposal.contactLastName}`
      // let clientName = newProposal.contactText
      let clientAddress = `${getContact.line01}${getContact.line01 && '-'}${getContact.line02}${
        getContact.line02 && '-'
      }${getContact.country}`
      getProposals.clientId = newProposal.contact
      getProposals.clientName = getContact.name
      getProposals.contactName = clientName
      getProposals.clientEmail = getContact.email
      getProposals.clientAddress = clientAddress
      getProposals.clientIdNumber = getContact.idnumber
      getProposals.clientVatNumber = getContact.vatNumber
      getProposals.clientCurrency = getContact.currency
      getProposals.clientTaxRate = getContact.taxRate
      getProposals.clientNote = getContact.note
      getProposals.isDetailed = newProposal.isDetailed

      getProposals.bankProfileNumber = newProposal.bankAccount
      if (newProposal.bankAccount === 'bankProfile1') {
        getProposals.proposalsBankProfileName = getCompany.bankProfileName
        getProposals.proposalsBank = getCompany.bank
        getProposals.proposalsIban = getCompany.iban
        getProposals.proposalsBic = getCompany.bic
        getProposals.proposalsAccountName = getCompany.accountName
      } else if (newProposal.bankAccount === 'bankProfile2') {
        getProposals.proposalsBankProfileName = getCompany.bankProfileName2
        getProposals.proposalsBank = getCompany.bank2
        getProposals.proposalsIban = getCompany.iban2
        getProposals.proposalsBic = getCompany.bic2
        getProposals.proposalsAccountName = getCompany.accountName2
      } else if (newProposal.bankAccount === 'bankProfile3') {
        getProposals.proposalsBankProfileName = getCompany.bankProfileName3
        getProposals.proposalsBank = getCompany.bank3
        getProposals.proposalsIban = getCompany.iban3
        getProposals.proposalsBic = getCompany.bic3
        getProposals.proposalsAccountName = getCompany.accountName3
      }
      if (getProposals.status === 'Generate') {
        for (let i = 0; i < newProposal.drafts.length; i++) {
          newProposal.drafts[i].taxAmount =
            (parseFloat(getContact.taxRate) / 100) * parseFloat(newProposal.drafts[i].amount)
          newProposal.drafts[i].amountAfterTax =
            parseFloat(newProposal.drafts[i].taxAmount) + parseFloat(newProposal.drafts[i].amount)
          newProposal.drafts[i].invoiceDate = dayjs(newProposal.drafts[i].invoiceDate).toISOString()
          newProposal.drafts[i].invoiceDueDate = dayjs(
            newProposal.drafts[i].invoiceDueDate
          ).toISOString()
          if (i === 0) {
            newProposal.drafts[i].balancePercentage =
              100 - parseFloat(newProposal.drafts[i].proposalAmountPercentage)
            newProposal.drafts[i].balanceAmount =
              parseFloat(getProposals.total) - parseFloat(newProposal.drafts[i].amount)
          } else {
            let totalAmount = 0
            let totalPercent = 0
            for (let j = 0; j <= i; j++) {
              totalAmount += parseFloat(newProposal.drafts[j].amount)
              totalPercent += parseFloat(newProposal.drafts[j].proposalAmountPercentage)
            }
            newProposal.drafts[i].balancePercentage = 100 - totalPercent
            newProposal.drafts[i].balanceAmount = parseFloat(getProposals.total) - totalAmount
          }
        }
      } else {
        for (let i = 0; i < newProposal.drafts.length; i++) {
          newProposal.drafts[i].invoiceId = getProposals.drafts[i].invoiceId
          newProposal.drafts[i].taxAmount =
            (parseFloat(getContact.taxRate) / 100) * parseFloat(newProposal.drafts[i].amount)
          newProposal.drafts[i].amountAfterTax =
            parseFloat(newProposal.drafts[i].taxAmount) + parseFloat(newProposal.drafts[i].amount)
          newProposal.drafts[i].generated =
            getProposals.drafts !== null ? getProposals.drafts[i].generated : false
          newProposal.drafts[i].invoiceDate = dayjs(newProposal.drafts[i].invoiceDate).toISOString()
          newProposal.drafts[i].invoiceDueDate = dayjs(
            newProposal.drafts[i].invoiceDueDate
          ).toISOString()
          if (i === 0) {
            newProposal.drafts[i].balancePercentage =
              100 - parseFloat(newProposal.drafts[i].proposalAmountPercentage)
            newProposal.drafts[i].balanceAmount =
              parseFloat(getProposals.total) - parseFloat(newProposal.drafts[i].amount)
          } else {
            let totalAmount = 0
            let totalPercent = 0
            for (let j = 0; j <= i; j++) {
              totalAmount += parseFloat(newProposal.drafts[j].amount)
              totalPercent += parseFloat(newProposal.drafts[j].proposalAmountPercentage)
            }
            newProposal.drafts[i].balancePercentage = 100 - totalPercent
            newProposal.drafts[i].balanceAmount = parseFloat(getProposals.total) - totalAmount
          }
        }
      }

      getProposals.drafts = newProposal.drafts
      getProposals.noofInvoices = newProposal.invoices
      getProposals.paymentTerms = newProposal.paymentTerms

      let updateContact = {}
      if (!isEqual(getContact.firstName, newProposal.contactFirstName)) {
        updateContact.firstName = newProposal.contactFirstName
      }
      if (!isEqual(getContact.lastName, newProposal.contactLastName)) {
        updateContact.lastName = newProposal.contactLastName
      }

      await API.graphql(graphqlOperation(mutations.updateProposals, { input: getProposals }))
      if (Object.keys(updateContact).length > 0) {
        updateContact.id = getContact.id
        updateContact._version = getContact._version
        await API.graphql(graphqlOperation(mutations.updateContact, { input: updateContact }))
      }
      await dispatch(fetchProposals())
      await dispatch(fetchContacts())
      makeSuccessToast(i18next.t('proposals:toasts.updatedProposal'))
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)
export const updateProposalFromPreview = createAsyncThunk(
  'proposals/updateProposalFromPreview',
  async ({ newProposal, currentProposal }, { dispatch, rejectWithValue, getState }) => {
    console.log('newProposal: ', newProposal)
    const { auth, settings } = getState()
    try {
      const {
        data: { getCompany },
      } = await API.graphql(graphqlOperation(queries.getCompany, { id: auth.userInfo.tenantId }))
      const {
        data: { getContact },
      } = await API.graphql(graphqlOperation(queries.getContact, { id: newProposal.contact }))
      const {
        data: { getProposals },
      } = await API.graphql(graphqlOperation(queries.getProposals, { id: currentProposal.id }))
      delete getProposals._deleted
      delete getProposals._lastChangedAt
      delete getProposals.quote
      delete getProposals.invoices
      // let clientName = `${getContact.firstName} ${getContact.lastName}`
      let clientName = `${newProposal.contactFirstName} ${newProposal.contactLastName}`
      // let clientName = newProposal.contactText
      let clientAddress = `${getContact.line01}${getContact.line01 && '-'}${getContact.line02}${
        getContact.line02 && '-'
      }${getContact.country}`
      getProposals.clientId = newProposal.contact
      getProposals.clientName = getContact.name
      getProposals.contactName = clientName
      getProposals.clientEmail = getContact.email
      getProposals.clientAddress = clientAddress
      getProposals.clientIdNumber = getContact.idnumber
      getProposals.clientVatNumber = getContact.vatNumber
      getProposals.clientCurrency = getContact.currency
      getProposals.clientTaxRate = getContact.taxRate
      getProposals.clientTaxType = getContact.taxType
      getProposals.clientTaxType = getContact.taxType
      getProposals.clientNote = getContact.note
      getProposals.isDetailed = newProposal.isDetailed

      getProposals.bankProfileNumber = newProposal.bankAccount
      if (newProposal.bankAccount === 'bankProfile1') {
        getProposals.proposalsBankProfileName = getCompany.bankProfileName
        getProposals.proposalsBank = getCompany.bank
        getProposals.proposalsIban = getCompany.iban
        getProposals.proposalsBic = getCompany.bic
        getProposals.proposalsAccountName = getCompany.accountName
      } else if (newProposal.bankAccount === 'bankProfile2') {
        getProposals.proposalsBankProfileName = getCompany.bankProfileName2
        getProposals.proposalsBank = getCompany.bank2
        getProposals.proposalsIban = getCompany.iban2
        getProposals.proposalsBic = getCompany.bic2
        getProposals.proposalsAccountName = getCompany.accountName2
      } else if (newProposal.bankAccount === 'bankProfile3') {
        getProposals.proposalsBankProfileName = getCompany.bankProfileName3
        getProposals.proposalsBank = getCompany.bank3
        getProposals.proposalsIban = getCompany.iban3
        getProposals.proposalsBic = getCompany.bic3
        getProposals.proposalsAccountName = getCompany.accountName3
      }

      if (getProposals.status === 'Generate') {
        for (let i = 0; i < newProposal.drafts.length; i++) {
          newProposal.drafts[i].taxAmount =
            (parseFloat(getContact.taxRate) / 100) * parseFloat(newProposal.drafts[i].amount)
          newProposal.drafts[i].amountAfterTax =
            parseFloat(newProposal.drafts[i].taxAmount) + parseFloat(newProposal.drafts[i].amount)
          newProposal.drafts[i].invoiceDate = dayjs(newProposal.drafts[i].invoiceDate).toISOString()
          newProposal.drafts[i].invoiceDueDate = dayjs(
            newProposal.drafts[i].invoiceDueDate
          ).toISOString()
          if (i === 0) {
            newProposal.drafts[i].balancePercentage =
              100 - parseFloat(newProposal.drafts[i].proposalAmountPercentage)
            newProposal.drafts[i].balanceAmount =
              parseFloat(getProposals.total) - parseFloat(newProposal.drafts[i].amount)
          } else {
            let totalAmount = 0
            let totalPercent = 0
            for (let j = 0; j <= i; j++) {
              totalAmount += parseFloat(newProposal.drafts[j].amount)
              totalPercent += parseFloat(newProposal.drafts[j].proposalAmountPercentage)
            }
            newProposal.drafts[i].balancePercentage = 100 - totalPercent
            newProposal.drafts[i].balanceAmount = parseFloat(getProposals.total) - totalAmount
          }
        }
      } else {
        for (let i = 0; i < newProposal.drafts.length; i++) {
          newProposal.drafts[i].invoiceId = getProposals.drafts[i].invoiceId
          newProposal.drafts[i].taxAmount =
            (parseFloat(getContact.taxRate) / 100) * parseFloat(newProposal.drafts[i].amount)
          newProposal.drafts[i].amountAfterTax =
            parseFloat(newProposal.drafts[i].taxAmount) + parseFloat(newProposal.drafts[i].amount)
          newProposal.drafts[i].generated =
            getProposals.drafts !== null ? getProposals.drafts[i].generated : false
          newProposal.drafts[i].invoiceDate = dayjs(newProposal.drafts[i].invoiceDate).toISOString()
          newProposal.drafts[i].invoiceDueDate = dayjs(
            newProposal.drafts[i].invoiceDueDate
          ).toISOString()
          if (i === 0) {
            newProposal.drafts[i].balancePercentage =
              100 - parseFloat(newProposal.drafts[i].proposalAmountPercentage)
            newProposal.drafts[i].balanceAmount =
              parseFloat(getProposals.total) - parseFloat(newProposal.drafts[i].amount)
          } else {
            let totalAmount = 0
            let totalPercent = 0
            for (let j = 0; j <= i; j++) {
              totalAmount += parseFloat(newProposal.drafts[j].amount)
              totalPercent += parseFloat(newProposal.drafts[j].proposalAmountPercentage)
            }
            newProposal.drafts[i].balancePercentage = 100 - totalPercent
            newProposal.drafts[i].balanceAmount = parseFloat(getProposals.total) - totalAmount
          }
        }
      }

      getProposals.drafts = newProposal.drafts
      getProposals.noofInvoices = newProposal.invoices
      getProposals.paymentTerms = newProposal.paymentTerms

      let updateContact = {}
      if (!isEqual(getContact.firstName, newProposal.contactFirstName)) {
        updateContact.firstName = newProposal.contactFirstName
      }
      if (!isEqual(getContact.lastName, newProposal.contactLastName)) {
        updateContact.lastName = newProposal.contactLastName
      }

      const {
        data: { updateProposals },
      } = await API.graphql(graphqlOperation(mutations.updateProposals, { input: getProposals }))
      dispatch(setCurrentProposal(updateProposals))
      if (Object.keys(updateContact).length > 0) {
        updateContact.id = getContact.id
        updateContact._version = getContact._version
        await API.graphql(graphqlOperation(mutations.updateContact, { input: updateContact }))
      }
      await dispatch(fetchProposals())
      await dispatch(fetchContacts())
      const {
        data: {
          invoicesByProposalId: { items },
        },
      } = await API.graphql(
        graphqlOperation(queries.invoicesByProposalId, {
          proposalId: getProposals.id,
          filter: { activeFlag: { eq: true } },
        })
      )
      let sortedInvoices = orderBy(items, ['invoiceNumber'], ['asc'])
      await dispatch(setCurrentProposalInvoices(sortedInvoices))
      makeSuccessToast(i18next.t('proposals:toasts.updatedProposal'))
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const updateProposalAmount = createAsyncThunk(
  'proposals/updateProposalAmount',
  async ({ currentProposal }, { dispatch, rejectWithValue }) => {
    try {
      const {
        data: { getQuote: originalQuote },
      } = await API.graphql(graphqlOperation(queries.getQuote, { id: currentProposal.quoteId }))
      let quoteInfo = await dispatch(fetchQuoteInfo({ quoteId: currentProposal.quoteId }))
      let recapQuoteInfo = await dispatch(
        fetchRecapQuoteInfo({ quoteId: currentProposal.quoteId, calculateRecapInfo: false })
      )
      const quoteTotals = quoteTotalsSummary(
        originalQuote,
        quoteInfo.payload,
        recapQuoteInfo.payload
      )
      await API.graphql(
        graphqlOperation(mutations.updateProposals, {
          input: {
            id: currentProposal.id,
            _version: currentProposal._version,
            total: quoteTotals.discount.totalAfterDiscount,
          },
        })
      )
      dispatch(fetchProposals())
      makeSuccessToast(i18next.t('proposals:toasts.updatedProposal'))
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const removeProposal = createAsyncThunk(
  'clients/removeProposal',
  async ({ quoteData, newQuote }, { getState, dispatch, rejectWithValue }) => {
    try {
      const {
        data: { getQuote: originalQuote2 },
      } = await API.graphql(graphqlOperation(queries.getQuote, { id: quoteData.id }))

      originalQuote2.isChecked = false
      delete originalQuote2.recapInfo
      delete originalQuote2.quoteInfo
      delete originalQuote2.proposals
      delete originalQuote2._deleted
      delete originalQuote2._lastChangedAt

      let newQuote = {}
      newQuote.id = quoteData.id
      newQuote._version = quoteData._version
      newQuote.isChecked = false

      const {
        data: { updateQuote: parsedUpdatedQuote },
      } = await API.graphql(graphqlOperation(mutations.updateQuote, { input: newQuote }))

      const {
        data: {
          proposalsByQuoteId: { items },
        },
      } = await API.graphql(
        graphqlOperation(queries.proposalsByQuoteId, {
          quoteId: quoteData.id,
          filter: { activeFlag: { eq: true } },
        })
      )
      delete items[0].quote
      delete items[0].invoices
      delete items[0]._deleted
      delete items[0]._lastChangedAt
      items[0].activeFlag = false
      items[0].quoteId = 'deleted'
      const {
        data: { updateProposals },
      } = await API.graphql(graphqlOperation(mutations.updateProposals, { input: { ...items[0] } }))
      await dispatch(fetchProjectChildren({ parentProjectId: quoteData.parentProjectId }))

      makeSuccessToast(i18next.t('proposals:toasts.removedProposal'))
    } catch (err) {
      makeErrorToast(msg)
      return rejectWithValue(err)
    }
  }
)

export const proposalsSlice = createSlice({
  name: 'proposals',
  initialState: {
    proposalsData: [],
    fetchProposalsApiStatus: 'idle',
    createProposalApiStatus: 'idle',
    removeProposalApiStatus: 'idle',
    updateProposalApiStatus: 'idle',
    updateProposalFromPreviewApiStatus: 'idle',
    updateProposalAmountApiStatus: 'idle',
    currentDraftIndex: null,
    proposalModalOpenStatus: false,
    pdfModalStatusOpen: false,
    currentProposal: null,
    currentProposalInvoices: null,
    error: null,
  },
  reducers: {
    setFetchProposalsApiStatus: (state, action) => {
      state.fetchProposalsApiStatus = 'loading'
    },
    setProposalModalOpenStatus: (state, action) => {
      state.proposalModalOpenStatus = action.payload.status
    },
    setPdfModalStatusOpen: (state, action) => {
      state.pdfModalStatusOpen = action.payload.status
    },
    setCurrentProposal: (state, action) => {
      state.currentProposal = action.payload
    },
    setCurrentProposalInvoices: (state, action) => {
      state.currentProposalInvoices = action.payload
    },
    setCurrentDraftIndex: (state, action) => {
      state.currentDraftIndex = action.payload
    },
  },
  extraReducers: {
    [fetchProposals.pending]: (state, action) => {
      state.fetchProposalsApiStatus = 'loading'
    },
    [fetchProposals.fulfilled]: (state, action) => {
      state.fetchProposalsApiStatus = 'succeeded'
      state.proposalsData = action.payload
    },
    [fetchProposals.rejected]: (state, action) => {
      state.fetchProposalsApiStatus = 'failed'
      state.error = action.error.message
    },
    [createProposal.pending]: (state, action) => {
      state.createProposalApiStatus = 'loading'
    },
    [createProposal.fulfilled]: (state, action) => {
      state.createProposalApiStatus = 'succeeded'
      state.proposalModalOpenStatus = false
    },
    [createProposal.rejected]: (state, action) => {
      state.createProposalApiStatus = 'failed'
      state.proposalModalOpenStatus = false
      state.error = action.error.message
    },
    [updateProposal.pending]: (state, action) => {
      state.updateProposalApiStatus = 'loading'
    },
    [updateProposal.fulfilled]: (state, action) => {
      state.updateProposalApiStatus = 'succeeded'
      state.proposalModalOpenStatus = false
    },
    [updateProposal.rejected]: (state, action) => {
      state.updateProposalApiStatus = 'failed'
      state.proposalModalOpenStatus = false
      state.error = action.error.message
    },
    [updateProposalFromPreview.pending]: (state, action) => {
      state.updateProposalFromPreviewApiStatus = 'loading'
    },
    [updateProposalFromPreview.fulfilled]: (state, action) => {
      state.updateProposalFromPreviewApiStatus = 'succeeded'
      state.pdfModalStatusOpen = true
    },
    [updateProposalFromPreview.rejected]: (state, action) => {
      state.updateProposalFromPreviewApiStatus = 'failed'
      state.pdfModalStatusOpen = false
      state.error = action.error.message
    },
    [updateProposalAmount.pending]: (state, action) => {
      state.updateProposalAmountApiStatus = 'loading'
    },
    [updateProposalAmount.fulfilled]: (state, action) => {
      state.updateProposalAmountApiStatus = 'succeeded'
    },
    [updateProposalAmount.rejected]: (state, action) => {
      state.updateProposalAmountApiStatus = 'failed'
      state.error = action.error.message
    },
    [removeProposal.pending]: (state, action) => {
      state.removeProposalApiStatus = 'loading'
    },
    [removeProposal.fulfilled]: (state, action) => {
      state.removeProposalApiStatus = 'succeeded'
    },
    [removeProposal.rejected]: (state, action) => {
      state.removeProposalApiStatus = 'failed'
      state.error = action.error.message
    },
  },
})

export const {
  setProposalModalOpenStatus,
  setCurrentProposal,
  setCurrentProposalInvoices,
  setFetchProposalsApiStatus,
  setPdfModalStatusOpen,
  setCurrentDraftIndex,
} = proposalsSlice.actions

export default proposalsSlice.reducer
